import React, {useState, useRef, useEffect,useCallback} from "react";
import './ai-chat-bot.css'
import { Input, Row, Avatar, Tooltip, Skeleton, Spin, Col } from 'antd';
import { SendOutlined, LoadingOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { ReactComponent as AiRedIcon } from '../../../assets/logos/ai icon red.svg';
import { getAiChatResponse, transcribeAudioToText } from './ai-chat-api'
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getIdeProjectLogo, trackEvent } from "../../utils/util-functions";
import { CopyOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { ReactComponent as ChatAiIconWhite } from '../../../assets/logos/chat-ai-icon-white.svg';
import { ReactComponent as ChatAiIconBlack } from '../../../assets/logos/chat-ai-icon-black.svg';
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { solarizedlight } from 'react-syntax-highlighter/dist/esm/styles/prism'; 
import { AudioRecorder } from 'react-audio-voice-recorder';

const { TextArea } = Input;
dayjs.extend(relativeTime);


const AiChatBot = ({ selectedFileId, ideProjectId, handleChatBotInsert, selectedTheme }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [chatData, setChatData] = useState([])
    const [instruction, setInstruction] = useState('')
    const [copied, setCopied] = useState(false);
    const [audioProcessing, setAudioProcessing] = useState(false);
    const chatRefs = useRef([]);

    const templatePrompts = [
        {
          keyword: "Debug",
          text: `find bugs in ${selectedFileId.fileName}`,
        },
        {
          keyword: "Explain",
          text: `explain how ${selectedFileId.fileName} works`,
        },
        {
          keyword: "Ask Ai",
          text: "How do I print hello world in python",
        },
    ];

    const updateTimeAgo = (timestamp) => {
        return dayjs(timestamp).fromNow();
    };


    const getChatResponse = async () => {
        if (!instruction.trim()) {
            return;
        }

        const reqObj = {
            ideProjectId: ideProjectId,
            fileId: selectedFileId.id,
            instruction: instruction
        };

        const questionObj = {
            'question': instruction,
            'askedTime': new Date(),
            'chatResponse': []
        };

        // First, add the questionObj with an empty response to the chatData array
        setChatData(prevChatDataArray => [...prevChatDataArray, questionObj]);

        setIsLoading(true);
        try {
            const response = await getAiChatResponse(reqObj);
            setIsLoading(false);

            if (response.data.statusCode === 200 && response.data.response) {
                const parsedResponse = parseResponse(response.data.response.response)

                setChatData(prevChatDataArray => {
                    const updatedChatDataArray = [...prevChatDataArray];
                    updatedChatDataArray[updatedChatDataArray.length - 1].chatResponse = parsedResponse;
                    return updatedChatDataArray;
                });
                setInstruction('')
            }
        } catch (error) {
            setIsLoading(false);
        }
    };


    const handleInstructionChange = (e) => {
        setInstruction(e.target.value);
      };

    const handleCopy = (codeToCopy) => {
       
        navigator.clipboard.writeText(codeToCopy).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
        
    };
    
    const handleInsert = (codeToInsert) => {
    
        handleChatBotInsert(codeToInsert)
    
    };

    const handleKeyDown = (event) => {
        
        if (event.key === 'Enter' && !isLoading) {

            event.preventDefault();

            trackEvent("ide_ai_chatbot_send_message", {
                ide_project_stack_name: "python",
                send_mode: "enter_key_pressed"
            });

            getChatResponse();
        }
    };
    
    const setChatRef = useCallback((element, index) => {
        chatRefs.current[index] = element;
    }, []);
    
    useEffect(() => {
        if (chatRefs.current.length > 0) {
            const lastChatRef = chatRefs.current[chatRefs.current.length - 1];
            if (lastChatRef) {
                lastChatRef.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [chatData]);

    const handleTemplatePrompt = (prompt) => {
        setInstruction(prompt);
    };

    const parseResponse = (response) => {
        const regexPattern = /```(?:\w+)?\n([\s\S]*?)```/g;
        let match = regexPattern.exec(response)
        const segments = [];
        let lastIndex = 0;
      
        while (match !== null) {
    
          if (lastIndex < match.index) {
            segments.push({ type: 'text', content: response.slice(lastIndex, match.index) });
          }
        
          segments.push({ type: 'code', content: match[1] });
          lastIndex = regexPattern.lastIndex;
          match = regexPattern.exec(response)
        }
      
        if (lastIndex < response.length) {
          segments.push({ type: 'text', content: response.slice(lastIndex) });
        }
      
        return segments;
      };

    return (
        <div className={`col border rounded p-3 ${selectedTheme === 'light' ? 'text-bg-light' : 'text-bg-dark'}`}
            style={{ height: 575, overflowY: "auto", overflowX: "auto", display: 'flex', flexDirection: 'column' }}
        >
            <div style={{marginBottom:'25px'}}>
            {
                chatData.length === 0 && (
                    <div>
                        <div className="cs-ai-chatbot">
                            <Row justify={"center"}>
                                {
                                    selectedTheme === 'light' ? (
                                        <span><ChatAiIconBlack /></span>
                                    ) : (
                                        <span><ChatAiIconWhite /></span>

                                    )
                                }
                            </Row>
                            <p className="text-center">
                                Get instant AI-powered insights on your code! Debug errors, ask questions, and get smart suggestions for your selected file, all with the power of CS AI.
                            </p>
                        </div>
                        <div className="template-prompts">
                            {templatePrompts.map((prompt, index) => (
                                <div
                                    onClick={() => handleTemplatePrompt(prompt.text)}
                                    key={index}
                                    className="template-prompt-individual"
                                >
                                    <AiRedIcon height="23px" />
                                    <span className="prompt-key-word">{prompt.keyword}</span> {prompt.text}
                                </div>
                            ))}
                        </div>
                    </div>
                )
            }

            {chatData &&
                <section >

                    <header>
                        {chatData.map((chat, index) => (
                           <div key={index} ref={(el) => setChatRef(el, index)}>
                                <AiRedIcon height={'23px'} />
                                &nbsp;&nbsp;
                                   CS Ai
                                <span>&nbsp;&nbsp;{updateTimeAgo(chat.askedTime)}</span>

                                <Row justify={"start"}>
                                    <div className="chat-question">{chat.question}</div>
                                </Row>

                                <div>

                                    {chat.chatResponse.map((segment, index) => {
                                        if (segment.type === 'code') {
                                            return (
                                                <div className="chat-content chat-message-console" key={index}>

                                                    <header className="snippet-header">
                                                        <Row justify={"space-between"}>
                                                            <span>
                                                                <Avatar src={getIdeProjectLogo('python')} /> Python
                                                            </span>
                                                            <Row>
                                                                <Tooltip title={copied ? "Copied" : "Copy"}>
                                                                    <span
                                                                        onClick={() => {
                                                                            trackEvent("ide_ai_chatbot_copy_code_clicked", {
                                                                                ide_project_stack_name: "python"
                                                                            });
                                                                            handleCopy(segment.content);
                                                                        }}
                                                                        style={{ cursor: 'pointer' }}>
                                                                        <CopyOutlined />&nbsp;Copy
                                                                    </span>
                                                                </Tooltip>
                                                                &nbsp;&nbsp;&nbsp;
                                                                <Tooltip title={'Insert code at cursor position'}>
                                                                    <span onClick={() => handleInsert(segment.content)} style={{ cursor: 'pointer', marginBottom: '15px' }}>
                                                                        <span><ArrowRightOutlined /></span>Insert
                                                                    </span>&nbsp;
                                                                </Tooltip>
                                                            </Row>
                                                        </Row>
                                                    </header>
                                                    <SyntaxHighlighter
                                                        key={index}
                                                        language={'python'} // Replace with detected language if available
                                                        style={solarizedlight}
                                                        customStyle={{ backgroundColor: '#ffffff' }}

                                                    >
                                                        {segment.content}
                                                    </SyntaxHighlighter>
                                                </div>
                                            );
                                        } else {
                                            return (
                                                <>
                                                    <ReactMarkdown
                                                        className="markdown"
                                                        key={index}>
                                                        {segment.content}
                                                    </ReactMarkdown>
                                                </>

                                            );
                                        }
                                    })}

                                    <br />
                                    <br />
                                </div>
                            </div>

                        ))}
                    </header>
                    {isLoading &&
                        <div>
                            <Skeleton active="true" />
                        </div>
                    }

                </section>

            }
            </div>
            

            <section className="chatbot-prompt" style={{ position: "absolute", bottom: 0, left: 0, right: 0,marginTop:'10px', padding: '10px', zIndex: 1000 }}>
            <Row gutter={8} align="middle">
                <Col flex="auto">
                    <TextArea
                        value={instruction}
                        placeholder="Type message..."
                        autoSize={{ minRows: 1, maxRows: 6 }}
                        onChange={handleInstructionChange}
                        style={{
                            border: 'none', 
                            boxShadow: 'none', 
                            backgroundColor: 'transparent'
                        }}
                        autoFocus
                        onKeyDown={handleKeyDown} 
                    />
                </Col>
                <Col>
                    {isLoading ? (
                        <ClockCircleOutlined />
                    ) : (
                        <SendOutlined onClick={() => {

                            trackEvent("ide_ai_chatbot_send_message", {
                                ide_project_stack_name: "python",
                                send_mode: "send_msg_btn_clicked"
                            });

                            getChatResponse();

                        }} />
                    )}
                </Col>
            </Row>
                    
                
            <footer>
                <Row>
                    {
                        audioProcessing ? (
                            <>
                                <Spin indicator={<LoadingOutlined spin />} size="large"/>
                                <span>Our CS Ai is processing your voice</span>
                            </>
                        ) :
                        (
                            <AudioRecorder
                                onRecordingComplete={fileObj => {
                                    trackEvent("ide_ai_chatbot_voice_chat_clicked", {
                                        ide_project_stack_name: "python"
                                    });
                                    setAudioProcessing(true);
                                    transcribeAudioToText(fileObj).then(res => {
                                        setAudioProcessing(false);
                                        if (res.data.statusCode === 200) {
                                            setInstruction(res.data.transcribedText);
                                        }
                                    });
                                }}
                                audioTrackConstraints={{
                                    noiseSuppression: true,
                                    echoCancellation: true,
                                }}
                                onNotAllowedOrFound={(err) => console.table(err)}
                                mediaRecorderOptions={{
                                    audioBitsPerSecond: 128000,
                                }}
                                showVisualizer={true}
                            />
                        )
                    }
                </Row>
            </footer>
            </section>
        </div>
    );
};

export default AiChatBot;
